import React, {Component} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import cl from 'classnames';
import {getScrollingElement, scrollToTop} from '~/utils/scrolling';
import {actions, select} from './connectors';
import {DECORATION_METHOD_DISPLAY_NAME} from '~/constants/ProductConstants';
import Color from '~/data/records/Color';
import MediaQueries, {breakpoint} from '~/constants/MediaQueries';
import {isEditDesignInCartMode} from '~/utils/url';
import {isEligibleForDecoFlex} from '~/utils/catalog';
// components
import {ColorSwatch} from '~/components/ColorSwatches';
import TrayButton from '~/components/ProductTray/TrayButton';
import YourItems from '~/shared/components/YourItems';
import {
  ArrowNextIcon,
  CardCreditCardIcon,
  Link,
  SaveIcon,
  PlusCircleIcon,
  Typography
} from '@customink/pigment-react';
import {metrics} from '~/vendor/metrics';
import {CiHeaderDesignName} from './CiHeaderDesignName';
import {getCheckoutSummaryLink} from '../../constants/CheckoutLink';

import './index.scss';

const propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      selection: PropTypes.object,
      style: PropTypes.object
    })
  ).isRequired,
  addCurrentDesignToCart: PropTypes.func,
  attemptFireNoSleeveImagesTag: PropTypes.func,
  cartItemCount: PropTypes.number,
  currentSelectionIndex: PropTypes.number,
  isCurrentDesignInCart: PropTypes.bool,
  isDesignNameInHeaderEnabled: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isEditing: PropTypes.bool,
  isFundraisingMode: PropTypes.bool,
  isMultiItemEligible: PropTypes.bool,
  isSleeveDecoEligible: PropTypes.bool,
  isSlideOver: PropTypes.bool,
  itemPrice: PropTypes.number,
  multiItemEnabled: PropTypes.bool,
  orderOptionsPath: PropTypes.string,
  product: PropTypes.object,
  productColor: PropTypes.instanceOf(Color),
  productName: PropTypes.string,
  saveDesign: PropTypes.func,
  selectedProducts: PropTypes.object,
  totalPrice: PropTypes.number,
  totalQuantities: PropTypes.number,
  onAddItemsClick: PropTypes.func,
  onAddToCartClick: PropTypes.func,
  onChangeProductClick: PropTypes.func,
  onDeleteProductClick: PropTypes.func,
  onFundraisingClick: PropTypes.func,
  onOrderClick: PropTypes.func,
  onPrintMethodClick: PropTypes.func,
  onProductColorClick: PropTypes.func,
  onSelectProductClick: PropTypes.func,
  onShareClick: PropTypes.func
};

/** @extends {Component<PropTypes.InferProps<typeof propTypes>>} */
export class ProductTray extends Component {
  static displayName = 'ProductTray';
  static propTypes = propTypes;

  static defaultProps = {
    isCurrentDesignInCart: false,
    productColor: new Color({red: 0, green: 0, blue: 0}),
    onOrderClick: () => {},
    onShareClick: () => {}
  };

  state = {
    isSticky: false,
    top: '100%',
    productTrayHeight: 80
  };

  componentDidMount() {
    document.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleResize);
    if (this.props.isDesignNameInHeaderEnabled) {
      document.querySelector('ci-header')?.classList.add('ndx-design-name');
    }
    this.updatePosition();
    this.addItemsWidth = this.getAddItemsWidth();
  }

  componentDidUpdate() {
    this.addItemsWidth = this.getAddItemsWidth();
  }

  componentWillUnmount() {
    document.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    window.cancelAnimationFrame(this.resizeRaf);
    window.cancelAnimationFrame(this.scrollRaf);
  }

  getAddItemsWidth = () => (this._addItems ? this._addItems.clientWidth : 0);

  checkSticky = scrollTop => {
    if (!this._node || this.props.isDisabled) return;
    const stickingPoint = this.getStickingPoint();
    const shouldStick = scrollTop >= stickingPoint;
    if (shouldStick !== this.state.isSticky) {
      this.setState({isSticky: shouldStick});
    }
  };

  isEditFromCart = () => {
    return this.props.isCurrentDesignInCart && isEditDesignInCartMode();
  };

  changeProduct = () => {
    this.props.onChangeProductClick();
  };

  getStickingPoint() {
    return this.state.isSticky
      ? window.innerHeight - this._node.offsetHeight
      : this._node.offsetTop;
  }

  updatePosition() {
    const offsetHeight = this.props.isDisabled
      ? window.innerHeight -
        this._node?.offsetHeight -
        this.state.productTrayHeight
      : window.innerHeight - this._node?.offsetHeight;
    const top = this._node ? offsetHeight : '100%';

    this.setState({top});
  }

  handleResize = () => {
    if (!this.isResizing) {
      this.isResizing = true;
      this.resizeRaf = window.requestAnimationFrame(() => {
        this.updatePosition();
        this.checkSticky(getScrollingElement().scrollTop);
        this.isResizing = false;
      });
    }
  };

  handleScroll = () => {
    if (!this.isScrolling) {
      this.isScrolling = true;
      this.scrollRaf = window.requestAnimationFrame(() => {
        this.checkSticky(getScrollingElement().scrollTop);
        this.isScrolling = false;
      });
    }
  };

  onSaveAndUpdateCartClick = () => {
    this.props.saveDesign().then(() => {
      this.props.addCurrentDesignToCart().then(() => {
        window.location.href = getCheckoutSummaryLink({
          src: 'updateDesignInCart',
          cart_source: 'lab_edit'
        });
      });
    });
  };

  scrollTopTriggerCallback(callback) {
    if (this.state.isSticky) {
      scrollToTop(document.body, () => setTimeout(() => callback?.(), 250));
    } else {
      callback?.();
    }
  }

  showMultiItemAdd = () =>
    this.props.isMultiItemEligible && this.props.cartItemCount > 0;

  showMultiItemUpdate = () =>
    this.props.isMultiItemEligible && this.isEditFromCart();

  renderOrderOptionsOrCartOptions = () => {
    const {items, onAddToCartClick, onOrderClick} = this.props;
    const multipleItems = items && items.length > 1;
    if (this.showMultiItemUpdate()) {
      return (
        <TrayButton
          data-testid="ProductTray Get Price"
          alternated
          key="order-cart-button"
          label="Save & Update Cart"
          multipleItems={multipleItems}
          onClick={() =>
            this.scrollTopTriggerCallback(this.onSaveAndUpdateCartClick)
          }
        />
      );
    } else if (this.showMultiItemAdd()) {
      return (
        <TrayButton
          data-testid="ProductTray Get Price"
          alternated
          icon={<CardCreditCardIcon />}
          key="order-cart-button"
          label="Get Price"
          multipleItems={multipleItems}
          onClick={() => this.scrollTopTriggerCallback(onAddToCartClick)}
        />
      );
    } else {
      return (
        <TrayButton
          data-testid="ProductTray Get Price"
          alternated
          icon={<CardCreditCardIcon />}
          key="order-cart-button"
          label="Get Price"
          multipleItems={multipleItems}
          onClick={() => this.scrollTopTriggerCallback(onOrderClick)}
        />
      );
    }
  };

  renderSaveShareButton = () => {
    const {items, onShareClick} = this.props;
    const multipleItems = items?.length > 1;

    if (this.props.isDesignNameInHeaderEnabled) {
      const portalRoot = document.querySelector('#ndx-save-share-portal');
      if (portalRoot) {
        return ReactDOM.createPortal(<CiHeaderDesignName />, portalRoot);
      }
      return null;
    }
    return this.isEditFromCart() ? (
      ''
    ) : (
      <TrayButton
        icon={<SaveIcon />}
        key="save-share-button"
        label="Save | Share"
        multipleItems={multipleItems}
        onClick={() => this.scrollTopTriggerCallback(onShareClick)}
      />
    );
  };

  renderProductTrayButtons = () => {
    const {
      items,
      isEditing,
      isFundraisingMode,
      onShareClick,
      onFundraisingClick
    } = this.props;
    const multipleItems = items && items.length > 1;
    const trayButtons = [
      this.renderSaveShareButton(),
      this.renderOrderOptionsOrCartOptions()
    ];

    return isFundraisingMode ? (
      <div
        className={cl('ndx-ProductTray-buttons', {isFlipped: isEditing})}
        data-testid="ndx-cta-fundraiser">
        <TrayButton
          icon={<SaveIcon />}
          label="Save | Share"
          multipleItems={multipleItems}
          onClick={() => this.scrollTopTriggerCallback(onShareClick)}
        />
        <TrayButton
          alternated
          icon={<ArrowNextIcon />}
          label="Continue"
          multipleItems={multipleItems}
          reversed
          onClick={() => this.scrollTopTriggerCallback(onFundraisingClick)}
        />
      </div>
    ) : (
      <div className={cl('ndx-ProductTray-buttons', {isFlipped: isEditing})}>
        {trayButtons}
      </div>
    );
  };

  renderDecoFlexDetails = () => {
    const {onPrintMethodClick, product, selectedProducts} = this.props;

    // Checks if we're in the test and product is eligible for decoflex
    if (!isEligibleForDecoFlex(selectedProducts)) {
      return null;
    }

    return (
      <>
        <span className="ndx-ProductTray-details-divider" />
        <span className="u-weight-xlight">
          {DECORATION_METHOD_DISPLAY_NAME[product.decoration_method]}
        </span>
        <Link
          button
          onClick={() => {
            metrics.fireEvent('print method', 'lab click', 'bottom bar');
            onPrintMethodClick();
          }}
          data-testid="ndx-cta-change-method">
          Change <span className="ndx-ProductTray-changeCTA">Method</span>
        </Link>
      </>
    );
  };

  render() {
    const {
      currentSelectionIndex,
      isDisabled,
      isEditing,
      isSleeveDecoEligible,
      isSlideOver,
      items,
      multiItemEnabled,
      productName,
      productColor,
      attemptFireNoSleeveImagesTag,
      onAddItemsClick,
      onSelectProductClick,
      onProductColorClick,
      onDeleteProductClick
    } = this.props;

    const {isSticky} = this.state;
    const style = isSticky ? null : {top: this.state.top};
    const disable = func => (!isDisabled || !isEditing ? func : undefined);
    const multipleItems = items && items.length > 1;
    const isCompactView = !breakpoint(MediaQueries.xxLargeAndAbove);

    return (
      <div
        ref={n => (this._node = n)}
        className={cl('ndx-ProductTray', {
          isSticky,
          isDisabled,
          isSlideOver
        })}
        style={style}>
        <div
          className="ndx-ProductTray-productContainer"
          style={
            isEditing
              ? {transform: `translateX(${-this.addItemsWidth}px)`}
              : null
          }>
          <div className={cl('ndx-ProductTray-yourItems', {isSlideOver})}>
            {multiItemEnabled && (
              <div
                ref={n => (this._addItems = n)}
                className={cl('ndx-ProductTray-addItems', {
                  isDisabled: isEditing
                })}>
                <TrayButton
                  className={cl('isAddItems')}
                  icon={<PlusCircleIcon />}
                  label="Add Products"
                  multipleItems={multipleItems}
                  onClick={onAddItemsClick}
                />
              </div>
            )}
            <YourItems
              attemptFireNoSleeveImagesTag={attemptFireNoSleeveImagesTag}
              disabled={isEditing}
              items={items}
              isSleeveDecoEligible={isSleeveDecoEligible}
              selectedItemIndex={currentSelectionIndex}
              onDelete={onDeleteProductClick}
              onSelect={disable(onSelectProductClick)}
            />
          </div>

          <div className="ndx-ProductTray-details">
            <Typography
              variant={isCompactView ? 'uiTextSmall' : 'uiTextDefault'}
              className={cl('ndx-ProductTray-productName', {isSlideOver})}>
              <span className="truncate" title={productName}>
                {productName}
              </span>
              {!isEditing && (
                <Link button variant="uiTextSmall" onClick={this.changeProduct}>
                  Change{' '}
                  <span className="ndx-ProductTray-changeCTA">Product</span>
                </Link>
              )}
            </Typography>
            <Typography
              variant="uiTextSmall"
              className="ndx-ProductTray-colors">
              <ColorSwatch
                sx={{marginRight: 1, height: '1.25rem', width: '1.25rem'}}
                color={productColor}
                onSwatchClick={disable(onProductColorClick)}
              />
              <Typography
                variant="uiTextSmall"
                className="truncate"
                sx={{marginRight: '0.75rem'}}>
                {productColor?.name}
              </Typography>
              {!isEditing && (
                <>
                  <Link button onClick={onProductColorClick}>
                    Change{' '}
                    <span className="ndx-ProductTray-changeCTA">Color</span>
                  </Link>
                  {this.renderDecoFlexDetails()}
                </>
              )}
            </Typography>
          </div>
        </div>
        {this.renderProductTrayButtons()}
      </div>
    );
  }
}

export default connect(select, actions)(ProductTray);
